import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, Link, useMediaQuery, useTheme } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import apiService from '../services/api';
import { AUTH_TESTIMONIALS, AUTH_SUBTITLE, authBgUrl, AUTH_BG_UNSPLASH_IDS } from '../config/authTestimonials';
import {
  BrandMark,
  DottedRule,
  HighlightedWord,
  INDIGO,
  INDIGO_DARK,
  INK,
  INK_SOFT,
  INTER_STACK,
  LeftPanel,
  MONO_STACK,
  SkuKicker,
} from '../components/auth/TicketTape';

/**
 * /forgot-password — request a reset link (DashFlow "ticket tape" theme).
 * The backend answers generically and only emails when the account exists.
 */
export const ForgotPassword: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const reducedMotion = useReducedMotion() ?? false;

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const fieldSx = {
    fontFamily: INTER_STACK,
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      fontFamily: INTER_STACK,
      bgcolor: '#fff',
      '& fieldset': { borderColor: 'rgba(15,23,42,0.18)' },
      '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.32)' },
      '&.Mui-focused fieldset': { borderColor: INDIGO, borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontFamily: INTER_STACK, color: INK_SOFT },
    '& .MuiInputLabel-root.Mui-focused': { color: INDIGO },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiService.forgotPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'No pudimos procesar el pedido. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const bgUrl = authBgUrl(AUTH_BG_UNSPLASH_IDS[0]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', fontFamily: INTER_STACK, bgcolor: '#ffffff' }}>
      {isDesktop && (
        <LeftPanel
          kicker="LOTE · RESET-042"
          headline={
            <>
              Recuperá tu acceso
              <br />
              en <HighlightedWord>un&nbsp;minuto</HighlightedWord>.
            </>
          }
          subtitle={AUTH_SUBTITLE}
          testimonials={AUTH_TESTIMONIALS}
          bgUrl={bgUrl}
          reducedMotion={reducedMotion}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          width: { xs: '100%', md: '50%' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2.5, sm: 5, md: 7, lg: 10 },
          py: { xs: 5, md: 6 },
          bgcolor: '#ffffff',
        }}
      >
        {!isDesktop && (
          <Box sx={{ alignSelf: 'flex-start', mb: 4 }}>
            <BrandMark color={INK} size="md" />
          </Box>
        )}

        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ mb: 2 }}>
            <SkuKicker label="SKU · RESET-042" />
          </Box>

          <Typography
            component="h2"
            sx={{ fontFamily: INTER_STACK, fontSize: { xs: 26, sm: 30 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, color: INK, mb: 1 }}
          >
            ¿Olvidaste tu <HighlightedWord>contraseña</HighlightedWord>?
          </Typography>
          <Typography sx={{ fontFamily: INTER_STACK, fontSize: 14.5, color: INK_SOFT, mb: 3 }}>
            {sent
              ? 'Si el email está registrado, te enviamos un enlace para crear una nueva contraseña.'
              : 'Ingresá tu email y te mandamos un enlace para crear una nueva.'}
          </Typography>

          <DottedRule my={0} />

          {error && (
            <Alert severity="error" sx={{ mt: 2.5, fontFamily: INTER_STACK, border: '1px solid rgba(220,38,38,0.25)', borderRadius: '5px' }}>
              {error}
            </Alert>
          )}

          {sent ? (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ border: `1px solid ${INDIGO}`, borderRadius: '6px', bgcolor: 'rgba(56,77,244,0.05)', p: 2.5, mb: 3 }}>
                <Typography sx={{ fontFamily: INTER_STACK, fontSize: 14.5, color: INK, lineHeight: 1.55 }}>
                  Revisá tu correo <strong>{email}</strong>. El enlace vence en 1 hora. Si no lo ves,
                  mirá la carpeta de spam.
                </Typography>
              </Box>
              <Link
                component={RouterLink}
                to="/login"
                sx={{ fontFamily: INTER_STACK, color: INDIGO, fontWeight: 600, textDecoration: 'none', borderBottom: `1px dotted ${INDIGO}`, pb: '1px', '&:hover': { borderBottomStyle: 'solid' } }}
              >
                ← Volver al inicio de sesión
              </Link>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2.5 }}>
              <TextField
                fullWidth
                label="Tu email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                required
                autoComplete="email"
                autoFocus
                sx={{ mb: 3, ...fieldSx }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !email}
                disableElevation
                sx={{
                  py: 1.4,
                  borderRadius: '5px',
                  fontFamily: INTER_STACK,
                  fontWeight: 600,
                  fontSize: 15,
                  textTransform: 'none',
                  bgcolor: INDIGO,
                  color: '#fff',
                  '&:hover': { bgcolor: INDIGO_DARK },
                }}
              >
                {loading ? 'Enviando…' : 'Enviar enlace'}
              </Button>

              <Typography sx={{ mt: 3, textAlign: 'center', fontFamily: INTER_STACK, fontSize: 14, color: INK_SOFT }}>
                ¿Te acordaste?{' '}
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{ fontFamily: INTER_STACK, color: INDIGO, fontWeight: 600, textDecoration: 'none', borderBottom: `1px dotted ${INDIGO}`, pb: '1px', '&:hover': { borderBottomStyle: 'solid' } }}
                >
                  Iniciá sesión →
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
